// The counts line, the reconciliation line and the headline (DESIGN D6; DESIGN-R2 B1, B3, B12). Every band count is
// shown - a zero is shown, never hidden, and drawn neutral, never green ("0 problems" is not a statement that anything
// is healthy):
//   Problems P · Recovered R · Not watched N · Housekeeping H                           (prominent)
//   Unknown U (includes the N not watched) · Control plane M · Downstream S
// The numbers are the server's band counts, verbatim; each lists its entries on hover (title) and on focus (a popover),
// and each is a BUTTON: pressing it scrolls its next entry's tile into view (inline and block centre - inside the
// plane's sideways scroller too), highlights it briefly and cycles on repeated presses. A press first clears every
// earlier highlight, and a visible status beside the chips says which count the lit tile belongs to ("Problems 1 of 1:
// <label>", DESIGN-R3 C12). Under them, the reconciliation:
// store rows -> checks -> landed / unplaced / invalid, and assets shown / hidden by state, from grid.reconciliation.
// If the band or the reconciliation contradicts itself, the page says so - it never shows the smaller number as the total.
import { useRef, useState } from "react";
import type { HealthResponse } from "./types";
import type { GridCtx } from "./ctx";
import { Contradiction, StateWord } from "./Tiles";
import { bandLook, downstreamEntries, plural, reconciliationView } from "./present";

type Style = "problem" | "unknown" | "recovered" | "zero" | "house" | "down";

interface Item {
  key: string;
  label: string;
  n: number;
  entries: string[];
  style: Style;
  suffix?: string;
}

/** The drawn element a band entry names: an asset's grid tile, or the strip that holds the unplaced / invalid entries. */
function entryElement(id: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  if (id === "unplaced") return document.querySelector<HTMLElement>('[data-testid="unplaced-checks"]') ?? document.querySelector<HTMLElement>('[data-testid="unplaced-strip"]');
  if (id === "invalid") return document.querySelector<HTMLElement>('[data-testid="invalid-entries"]') ?? document.querySelector<HTMLElement>('[data-testid="unplaced-strip"]');
  const q = id.replace(/["\\]/g, "\\$&");
  // DESIGN-R3 C5: "invalid:<asset id>" - the strip's line that counts under it (the answer's grid.invalid[].entry), else the
  // line naming that asset id (an answer without `entry`)
  if (id.startsWith("invalid:")) {
    const iq = id.slice("invalid:".length).replace(/["\\]/g, "\\$&");
    return (
      document.querySelector<HTMLElement>(`[data-testid="invalid-entries"] [data-invalid-entry="${q}"]`) ??
      document.querySelector<HTMLElement>(`[data-testid="invalid-entries"] [data-invalid="${iq}"]`) ??
      document.querySelector<HTMLElement>('[data-testid="invalid-entries"]') ??
      document.querySelector<HTMLElement>('[data-testid="unplaced-strip"]')
    );
  }
  return document.querySelector<HTMLElement>(`[data-testid="asset-tile"][data-asset-id="${q}"]`);
}

const FLASH_MS = 1600;

function Count({ it, first, ctx, onPress }: { it: Item; first: boolean; ctx: GridCtx; onPress: (it: Item) => void }) {
  const names = it.entries.map(ctx.labelOf);
  const popId = `ph-count-pop-${it.key}`;
  return (
    <span className="ph-count-item">
      {first ? null : (
        <span className="ph-sep" aria-hidden="true">
          {" · "}
        </span>
      )}
      <button
        type="button"
        className={`ph-count ph-count-${it.style}`}
        data-count={it.key}
        data-value={it.n}
        data-testid={`count-${it.key}`}
        title={`${it.label} ${it.n}: ${names.length > 0 ? names.join(", ") : "none"}${names.length > 0 ? " - press to show each on the grid" : ""}`}
        aria-describedby={popId}
        onClick={() => onPress(it)}
      >
        <span className="ph-count-label">{it.label}</span> <span className="ph-count-n">{it.n}</span>
        {it.suffix ? <span className="ph-count-suffix"> {it.suffix}</span> : null}
        <span className="ph-count-pop" role="tooltip" id={popId}>
          {names.length > 0 ? names.join(", ") : "none"}
        </span>
      </button>
    </span>
  );
}

export function CountsLine({ data, ctx }: { data: HealthResponse; ctx: GridCtx }) {
  const b = data.band;
  const unwatched = b.unknown_causes.find(c => c.key === "unwatched");
  const problemEntries = [...new Set([...b.primaries, ...b.possibly_consequent])];
  const down = downstreamEntries(data);
  const suppressed = typeof b.suppressed === "number" ? b.suppressed : 0;
  const main: Item[] = [
    { key: "problems", label: "Problems", n: b.problems, entries: problemEntries, style: b.problems > 0 ? "problem" : "zero" },
    { key: "recovered", label: "Recovered", n: b.recovered, entries: b.recovered_entries, style: b.recovered > 0 ? "recovered" : "zero" },
    { key: "not_watched", label: "Not watched", n: b.not_watched, entries: unwatched?.entries ?? [], style: b.not_watched > 0 ? "unknown" : "zero" },
    { key: "housekeeping", label: "Housekeeping", n: b.housekeeping, entries: b.housekeeping_entries, style: "house" }
  ];
  const second: Item[] = [
    {
      key: "unknown",
      label: "Unknown",
      n: b.unknown,
      entries: b.unknown_causes.flatMap(c => c.entries),
      style: b.unknown > 0 ? "unknown" : "zero",
      suffix: `(includes the ${b.not_watched} not watched)`
    },
    { key: "control", label: "Control plane", n: b.monitoring_problems, entries: b.monitoring, style: b.monitoring_problems > 0 ? "problem" : "zero" },
    { key: "suppressed", label: "Downstream", n: suppressed, entries: down, style: suppressed > 0 ? "down" : "zero" }
  ];
  const contra: string[] = [];
  const causeSum = b.unknown_causes.reduce((s, c) => s + c.count, 0);
  if ((unwatched?.count ?? 0) !== b.not_watched) {
    contra.push(`the band counts ${b.not_watched} not watched, but its not-watched cause line counts ${unwatched?.count ?? 0} - the answer contradicts itself`);
  }
  if (b.not_watched > b.unknown) contra.push(`the band counts ${b.not_watched} not watched but only ${b.unknown} UNKNOWN - not watched is part of UNKNOWN`);
  if (causeSum !== b.unknown) {
    contra.push(`the unknown causes add up to ${causeSum}, but the band counts ${b.unknown} UNKNOWN - ${Math.abs(b.unknown - causeSum)} ${b.unknown > causeSum ? "UNKNOWN entries are in no cause line" : "cause entries too many"}`);
  }
  if (problemEntries.length !== b.problems) contra.push(`the band counts ${plural(b.problems, "problem")} but names ${problemEntries.length}`);
  if (b.recovered_entries.length !== b.recovered) contra.push(`the band counts ${b.recovered} recovered but names ${b.recovered_entries.length}`);
  if (b.monitoring.length !== b.monitoring_problems) contra.push(`the band counts ${b.monitoring_problems} control-plane problems but names ${b.monitoring.length}`);
  if (typeof b.suppressed !== "number") contra.push("the band carries no downstream (suppressed) count - the page shows 0 and cannot vouch for it");
  if (down.length !== suppressed) contra.push(`the band counts ${suppressed} downstream, but ${plural(down.length, "drawn asset")} ${down.length === 1 ? "is" : "are"} collapsed under a primary`);

  const cursor = useRef<Record<string, number>>({});
  const flashTimer = useRef<number | undefined>(undefined);
  const [status, setStatus] = useState("");
  const press = (it: Item) => {
    // DESIGN-R3 C12: a press first clears EVERY earlier highlight (of any count), so the one lit tile is this count's
    if (typeof document !== "undefined") document.querySelectorAll(".ph-flash").forEach(e => e.classList.remove("ph-flash"));
    window.clearTimeout(flashTimer.current);
    if (it.entries.length === 0) {
      setStatus(`${it.label} ${it.n}: nothing to show`);
      return;
    }
    const start = cursor.current[it.key] ?? 0;
    for (let k = 0; k < it.entries.length; k++) {
      const i = (start + k) % it.entries.length;
      const el = entryElement(it.entries[i]);
      if (!el) continue;
      cursor.current[it.key] = i + 1;
      el.scrollIntoView({ block: "center", inline: "center" });
      el.classList.add("ph-flash");
      flashTimer.current = window.setTimeout(() => el.classList.remove("ph-flash"), FLASH_MS);
      // DESIGN-R3 C12: "<Count> n of N: <label>", visibly next to the chips
      setStatus(`${it.label} ${i + 1} of ${it.entries.length}: ${ctx.labelOf(it.entries[i])}`);
      return;
    }
    setStatus(`${it.label}: none of its ${plural(it.entries.length, "entry", "entries")} is drawn on the page`);
  };

  const rec = reconciliationView(data);
  return (
    <nav className="ph-counts" aria-label="counts" data-testid="counts-line">
      <p className="ph-counts-line ph-counts-main">
        {main.map((it, i) => (
          <Count key={it.key} it={it} first={i === 0} ctx={ctx} onPress={press} />
        ))}
      </p>
      <p className="ph-counts-line ph-counts-second">
        {second.map((it, i) => (
          <Count key={it.key} it={it} first={i === 0} ctx={ctx} onPress={press} />
        ))}
      </p>
      <p className="ph-count-status" role="status" aria-live="polite" data-testid="count-status">
        {status}
      </p>
      {contra.map(c => (
        <p key={c} className="ph-contra" data-testid="counts-contradiction">
          {c}
        </p>
      ))}
      <div className={`ph-recon${rec.known ? "" : " ph-k-unknown"}`} data-testid="reconciliation" data-known={rec.known ? "true" : "false"}>
        {rec.lines.map((l, i) => (
          <p key={i} className="ph-recon-line" data-recon-line={i + 1}>
            {rec.known ? null : <StateWord lk={{ kind: "unknown", word: "UNKNOWN", contradiction: null }} />} {rec.known ? l : l.replace(/^UNKNOWN - /, "")}
          </p>
        ))}
        {rec.contradictions.map(c => (
          <p key={c} className="ph-contra ph-recon-contra" data-testid="recon-contradiction">
            {c}
          </p>
        ))}
      </div>
    </nav>
  );
}

/** The server's headline sentence, verbatim, in the band's own style (a green band the same answer contradicts is not
 *  shown as healthy - bandLook says why). */
export function Headline({ data, ctx }: { data: HealthResponse; ctx: GridCtx }) {
  const lk = bandLook(data, ctx.placement);
  return (
    <section className={`ph-headline ph-k-${lk.kind}`} data-testid="headline" data-kind={lk.kind} aria-label="verdict">
      <p className="ph-headline-text">
        <StateWord lk={lk} /> {data.band.headline}
      </p>
      <Contradiction lk={lk} />
    </section>
  );
}
